/**
 * 语音翻译 composable
 * 集成 Azure Speech Translation SDK，实现一源多目标实时翻译
 *
 * Azure 语音翻译的 AddTargetLanguage 需使用目标语言简写（如 en、zh-Hans），
 * 与后端 cr_language 的 NA_NAME / 接口返回的 shortCode、azureTargetCode 一致。
 */

import { ref } from 'vue'
import { isL1Error } from '@/shared/api/l1-error'
import { appendFragment, getSpeechToken, publishPreview, type LiveTextPayload } from '../api'
import {
  applySpeechTranslationSegmentation,
  buildTranslationsFromAzureCodes,
  resolveEffectiveAzureTargetCodes,
} from '../utils/azure-speech-sdk-shared'

export const TOKEN_REFRESH_SUCCESS_MS = 4 * 60 * 1000
export const TOKEN_REFRESH_RETRY_MS = 15 * 1000
export const TOKEN_FORCE_REBUILD_MS = 9 * 60 * 1000
export const RECOGNIZER_STOP_TIMEOUT_MS = 3000
export const FRAGMENT_REQUEST_TIMEOUT_MS = 10_000
const FRAGMENT_ATTEMPTS = 3

export type SpeechDesiredState = 'running' | 'stopping' | 'reconnecting' | 'stopped'

export interface SpeechStartArgs {
  audioStream: MediaStream
  sourceLanguage: string
  targetLanguages: string[]
  targetLanguageAzureCodes?: string[]
}

export interface UseSpeechTranslationOptions {
  appId: string
}

export function speechTransportControls(state: SpeechDesiredState): {
  showResume: boolean
  disabled: boolean
} {
  return {
    showResume: state === 'stopped',
    disabled: state === 'stopping',
  }
}

interface RecognizerLike {
  authorizationToken: string
  recognizing: ((sender: unknown, event: RecognizingEvent) => void) | null
  recognized: ((sender: unknown, event: RecognizedEvent) => void) | null
  canceled: ((sender: unknown, event: CanceledEvent) => void) | null
  sessionStopped: ((sender: unknown, event: unknown) => void) | null
  startContinuousRecognitionAsync(cb?: () => void, err?: (error: string) => void): void
  stopContinuousRecognitionAsync(cb?: () => void, err?: (error: string) => void): void
  close(): void
}

interface RecognizingEvent {
  result: { reason: number; text: string; translations: Map<string, string> }
}

interface RecognizedEvent {
  result: {
    reason: number
    text: string
    translations: Map<string, string>
    errorDetails?: string
  }
}

interface CanceledEvent {
  reason: number
}

interface SpeechSdk {
  SpeechTranslationConfig: {
    fromAuthorizationToken(token: string, region: string): SpeechConfigLike
  }
  AudioConfig: { fromStreamInput(stream: MediaStream): unknown }
  TranslationRecognizer: new (config: SpeechConfigLike, audio: unknown) => RecognizerLike
  ResultReason: { TranslatingSpeech: number; TranslatedSpeech: number; Canceled: number; NoMatch: number }
  CancellationReason: { Error: number; EndOfStream: number }
  PropertyId: Record<string, unknown>
}

interface SpeechConfigLike {
  speechRecognitionLanguage: string
  addTargetLanguage(code: string): void
  setProperty(id: unknown, value: string): void
}

function invokeSdk(call: (cb: () => void, err: (error: string) => void) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      call(
        () => resolve(),
        (error) => reject(new Error(error || 'speech sdk error')),
      )
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  })
}

function withTimeout(promise: Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function fragmentRetryable(error: unknown): boolean {
  if (!isL1Error(error)) return true
  return error.httpStatus === 0 || error.httpStatus >= 500
}

export function useSpeechTranslation(options: UseSpeechTranslationOptions) {
  const isRecognizing = ref(false)
  const error = ref<string | null>(null)
  const lastSource = ref('')
  const lastTranslations = ref<Record<string, string>>({})
  const fragments = ref<Array<{ sourceText: string; translations: Record<string, string> }>>([])
  const desiredState = ref<SpeechDesiredState>('stopped')

  let recognizer: RecognizerLike | null = null
  let activeStart: SpeechStartArgs | null = null
  let epoch = 0
  let revision = 0
  let pushEnabled = false
  let previewInFlight = false
  let previewDirty = false
  let previewAbort: AbortController | null = null
  let reconnectLock = false
  let rebuildSuppressed = false
  let tokenAcceptedAt = 0
  let tokenTimer: ReturnType<typeof setTimeout> | null = null
  let rebuildTimer: ReturnType<typeof setTimeout> | null = null

  function clearSpeechTimers() {
    if (tokenTimer) {
      clearTimeout(tokenTimer)
      tokenTimer = null
    }
    if (rebuildTimer) {
      clearTimeout(rebuildTimer)
      rebuildTimer = null
    }
  }

  function suppressRebuild() {
    rebuildSuppressed = true
    clearSpeechTimers()
  }

  function scheduleTokenRefresh(delay: number) {
    if (rebuildSuppressed) return
    if (tokenTimer) clearTimeout(tokenTimer)
    tokenTimer = setTimeout(() => {
      tokenTimer = null
      void refreshToken()
    }, delay)
  }

  function scheduleRebuild() {
    if (rebuildSuppressed) return
    if (rebuildTimer) clearTimeout(rebuildTimer)
    rebuildTimer = setTimeout(() => {
      rebuildTimer = null
      void rebuildRecognizer()
    }, TOKEN_REFRESH_RETRY_MS)
  }

  function acceptToken(at = Date.now()) {
    tokenAcceptedAt = at
    scheduleTokenRefresh(TOKEN_REFRESH_SUCCESS_MS)
  }

  async function refreshToken() {
    if (rebuildSuppressed || desiredState.value !== 'running' || !recognizer) return
    if (Date.now() - tokenAcceptedAt >= TOKEN_FORCE_REBUILD_MS && !isRecognizing.value) {
      void rebuildRecognizer()
      return
    }
    const current = recognizer
    try {
      const { token } = await getSpeechToken()
      if (rebuildSuppressed || current !== recognizer || desiredState.value !== 'running') return
      if (!token) {
        scheduleTokenRefresh(TOKEN_REFRESH_RETRY_MS)
        return
      }
      current.authorizationToken = token
      acceptToken()
    } catch {
      if (rebuildSuppressed || current !== recognizer || desiredState.value !== 'running') return
      scheduleTokenRefresh(TOKEN_REFRESH_RETRY_MS)
    }
  }

  function abortPreview() {
    previewDirty = false
    previewAbort?.abort()
    previewAbort = null
  }

  async function flushPreview() {
    if (previewInFlight || !pushEnabled || !previewDirty) return
    previewDirty = false
    previewInFlight = true
    const controller = new AbortController()
    previewAbort = controller
    revision += 1
    const sentRevision = revision
    const payload: LiveTextPayload = {
      sourceText: lastSource.value,
      translations: { ...lastTranslations.value },
      revision: sentRevision,
    }
    try {
      await publishPreview(options.appId, payload, {
        signal: controller.signal,
        skipErrorToast: true,
      })
    } catch {
      // 中间态失败不重试、不提示
    } finally {
      previewInFlight = false
      if (previewAbort === controller) previewAbort = null
      if (previewDirty && pushEnabled) void flushPreview()
    }
  }

  function schedulePreviewPush() {
    previewDirty = true
    void flushPreview()
  }

  async function sendFragment(payload: LiveTextPayload) {
    let lastError: unknown
    for (let attempt = 0; attempt < FRAGMENT_ATTEMPTS; attempt += 1) {
      try {
        await appendFragment(options.appId, payload, {
          timeout: FRAGMENT_REQUEST_TIMEOUT_MS,
          skipErrorToast: true,
        })
        return
      } catch (caught) {
        lastError = caught
        if (!fragmentRetryable(caught) || attempt === FRAGMENT_ATTEMPTS - 1) break
      }
    }
    error.value = isL1Error(lastError) && lastError.message ? lastError.message : '定稿发送失败'
  }

  async function attachRecognizer(token: string, region: string, args: SpeechStartArgs) {
    const speechsdk = (await import('microsoft-cognitiveservices-speech-sdk')) as unknown as SpeechSdk
    const config = speechsdk.SpeechTranslationConfig.fromAuthorizationToken(token, region)
    config.speechRecognitionLanguage = args.sourceLanguage
    const effectiveAzureCodes = resolveEffectiveAzureTargetCodes(
      args.targetLanguages,
      args.targetLanguageAzureCodes,
    )
    const azureTargets = new Set<string>()
    for (const code of effectiveAzureCodes) {
      if (code && !azureTargets.has(code)) {
        azureTargets.add(code)
        config.addTargetLanguage(code)
      }
    }
    applySpeechTranslationSegmentation(config, speechsdk)
    config.setProperty(speechsdk.PropertyId.Recognizer_StopTimeoutMs, String(RECOGNIZER_STOP_TIMEOUT_MS))

    const audioConfig = speechsdk.AudioConfig.fromStreamInput(args.audioStream)
    const next = new speechsdk.TranslationRecognizer(config, audioConfig)
    const registeredEpoch = epoch

    next.recognizing = (_sender, event) => {
      if (registeredEpoch !== epoch || !pushEnabled) return
      if (event.result.reason !== speechsdk.ResultReason.TranslatingSpeech) return
      lastSource.value = event.result.text
      lastTranslations.value = buildTranslationsFromAzureCodes(
        event.result.translations,
        args.targetLanguages,
        effectiveAzureCodes,
      )
      schedulePreviewPush()
    }

    next.recognized = (_sender, event) => {
      if (registeredEpoch !== epoch || !pushEnabled) return
      const result = event.result
      if (result.reason === speechsdk.ResultReason.Canceled) {
        error.value = result.errorDetails || '识别已取消'
        return
      }
      if (result.reason === speechsdk.ResultReason.NoMatch) return
      if (result.reason !== speechsdk.ResultReason.TranslatedSpeech || !result.text) return
      abortPreview()
      const fragmentRevision = revision
      revision += 1
      const translations = buildTranslationsFromAzureCodes(
        result.translations,
        args.targetLanguages,
        effectiveAzureCodes,
      )
      fragments.value.push({ sourceText: result.text, translations })
      void sendFragment({
        sourceText: result.text,
        translations,
        revision: fragmentRevision,
        fragmentId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${fragmentRevision}`,
      })
    }

    next.canceled = (_sender, event) => {
      if (registeredEpoch !== epoch || rebuildSuppressed) return
      if (
        event.reason !== speechsdk.CancellationReason.Error &&
        event.reason !== speechsdk.CancellationReason.EndOfStream
      ) {
        return
      }
      void rebuildRecognizer()
    }

    next.sessionStopped = () => {
      if (registeredEpoch !== epoch || rebuildSuppressed) return
      void rebuildRecognizer()
    }

    await invokeSdk((cb, err) => next.startContinuousRecognitionAsync(cb, err))
    if (registeredEpoch !== epoch || !pushEnabled || rebuildSuppressed) {
      try {
        next.close()
      } catch {
        // 启动完成时用户已经停下
      }
      return
    }
    recognizer = next
    isRecognizing.value = true
  }

  async function start(args: SpeechStartArgs) {
    error.value = null
    rebuildSuppressed = false
    clearSpeechTimers()
    activeStart = args
    pushEnabled = true
    const { token, region } = await getSpeechToken()
    if (!token || !region) {
      pushEnabled = false
      error.value = '无法获取语音服务 token'
      return
    }
    await attachRecognizer(token, region, args)
    if (isRecognizing.value && !rebuildSuppressed) acceptToken()
  }

  async function stop(opts?: { clearPreview?: boolean }) {
    const clearPreview = opts?.clearPreview !== false
    epoch += 1
    pushEnabled = false
    clearSpeechTimers()
    abortPreview()
    const current = recognizer
    recognizer = null
    try {
      if (current) {
        try {
          await withTimeout(
            invokeSdk((cb, err) => current.stopContinuousRecognitionAsync(cb, err)),
            RECOGNIZER_STOP_TIMEOUT_MS,
          )
        } catch {
          // 超时或 SDK 拒绝都继续关闭
        }
        try {
          current.close()
        } catch {
          // already disposed
        }
      }
    } finally {
      isRecognizing.value = false
    }
    if (!clearPreview) return
    revision += 1
    try {
      await publishPreview(
        options.appId,
        { sourceText: '', translations: {}, revision },
        { skipErrorToast: true },
      )
    } catch {
      // 清空失败不影响暂停结果
    }
  }

  async function rebuildRecognizer() {
    if (rebuildSuppressed || reconnectLock || desiredState.value === 'stopped') return
    const args = activeStart
    reconnectLock = true
    if (rebuildTimer) {
      clearTimeout(rebuildTimer)
      rebuildTimer = null
    }
    desiredState.value = 'reconnecting'
    try {
      await stop({ clearPreview: false })
      if (rebuildSuppressed) return
      if (!args) {
        scheduleRebuild()
        return
      }
      let token = ''
      let region = ''
      try {
        const issued = await getSpeechToken()
        token = issued.token
        region = issued.region
      } catch {
        if (!rebuildSuppressed) scheduleRebuild()
        return
      }
      if (rebuildSuppressed) return
      if (!token || !region) {
        scheduleRebuild()
        return
      }
      pushEnabled = true
      activeStart = args
      try {
        await attachRecognizer(token, region, args)
      } catch {
        if (!rebuildSuppressed) scheduleRebuild()
        return
      }
      if (rebuildSuppressed) return
      if (isRecognizing.value) {
        desiredState.value = 'running'
        acceptToken()
        return
      }
      scheduleRebuild()
    } finally {
      reconnectLock = false
    }
  }

  return {
    isRecognizing,
    error,
    lastSource,
    lastTranslations,
    fragments,
    desiredState,
    start,
    stop,
    suppressRebuild,
  }
}
